import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';
import { FiscalMonthSettings } from '../stores/fiscalMonthSettingsStore';

export interface FiscalMonthSettingsResponse {
  success: boolean;
  data: FiscalMonthSettings;
  message?: string;
}

class FiscalMonthSettingsService {
  private apiRoot = configs.apiRoot;

  /**
   * Get fiscal month settings for current tenant
   */
  async getSettings(): Promise<FiscalMonthSettings> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/report-settings/fiscal-month`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      // If 404, return default settings (settings don't exist yet)
      if (response.status === 404) {
        return { startDay: 1, enabled: false };
      }
      
      // Try to parse error message if available
      try {
        const error = await response.json();
        throw new Error(
          error.message || `Failed to fetch fiscal month settings: ${response.statusText}`
        );
      } catch (e) {
        throw new Error(`Failed to fetch fiscal month settings: ${response.statusText}`);
      }
    }

    const result: FiscalMonthSettingsResponse = await response.json();
    return result.data;
  }

  /**
   * Update fiscal month settings for current tenant
   */
  async updateSettings(
    updates: Partial<FiscalMonthSettings> | FiscalMonthSettings
  ): Promise<FiscalMonthSettings> {
    // Ensure we always send both enabled and startDay
    // The store already ensures both fields are present, but double-check here
    const payload: FiscalMonthSettings = {
      enabled: updates.enabled !== undefined ? updates.enabled : false,
      startDay: updates.startDay !== undefined ? updates.startDay : 1,
    };
    
    console.log('[FiscalMonthSettingsService] Sending update', { payload, originalUpdates: updates });
    
    const response = await authenticatedFetch(
      `${this.apiRoot}/report-settings/fiscal-month`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to update fiscal month settings: ${response.statusText}`
      );
    }

    const result: FiscalMonthSettingsResponse = await response.json();
    console.log('[FiscalMonthSettingsService] Received response', { result });
    return result.data;
  }
}

export const fiscalMonthSettingsService = new FiscalMonthSettingsService();

